import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { CSSProperties } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import type { So5FixtureEvent } from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { LineupLocked } from '@sorare/core/src/atoms/icons/LineupLocked';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyS, LabelM, LabelS } from '@sorare/core/src/atoms/typography';
import { Ellipsis } from '@sorare/core/src/atoms/typography/Ellipsis';
import { TeamAvatar } from '@sorare/core/src/components/club/TeamAvatar';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { isType } from '@sorare/core/src/lib/gql';
import { anyPositionShortNames } from '@sorare/core/src/lib/players';

import {
  Cell,
  SearchTableFirstCell,
  SearchTableRow,
} from '@sorare/marketplace/src/components/searchPlayerTable/SearchTable/ui';

import { PlayerAvatar } from 'components/player/PlayerAvatar';

import type { PlayerScoreView } from '../types';
import { BaseballScoreCells } from './BaseballScoreCells';
import { FootballScoreCells } from './FootballScoreCells';
import { NBAScoreCells } from './NBAScoreCells';
import type { FixtureTopPlayersTableRow_fixturePlayer } from './__generated__/index.graphql';

const StartStatsCell = styled(Cell)`
  justify-content: flex-start;
`;
const PlayerCell = styled(StartStatsCell)`
  padding: var(--intermediate-unit) 0;
`;
const StyledSearchTableRow = styled(SearchTableRow)`
  &:hover {
    --current-row-color: var(--c-nd-50-opaque);
  }
`;

type Props = {
  index: number;
  so5FixturePlayer: FixtureTopPlayersTableRow_fixturePlayer;
  statsManager: PlayerScoreView;
  isUpcomingFixture?: boolean;
  onClick?: () => void;
  onListingsClick: () => void;
  eventType?: So5FixtureEvent;
};
export const FixtureTopPlayersTableRow = ({
  index,
  so5FixturePlayer,
  statsManager,
  isUpcomingFixture,
  onClick,
  onListingsClick,
  eventType,
}: Props) => {
  const {
    flags: { useNbaTopPlayers = false },
  } = useFeatureFlags();
  const { id, eligibleSo5Scores, player, score } = so5FixturePlayer || {};
  const { positionTyped, anyPlayerGameStats } = eligibleSo5Scores.at(0) || {};

  const cells = (so5Score => {
    if (isType(so5Score, 'BaseballPlayerGameScore')) {
      return (
        <BaseballScoreCells
          player={player}
          totalScore={score}
          eligibleSo5Scores={so5FixturePlayer.eligibleSo5Scores}
          statsManager={statsManager}
          isUpcomingFixture={isUpcomingFixture}
          onListingsClick={onListingsClick}
        />
      );
    }
    if (useNbaTopPlayers && isType(so5Score, 'BasketballPlayerGameScore')) {
      return (
        <NBAScoreCells
          player={player}
          eligibleSo5Scores={so5FixturePlayer.eligibleSo5Scores}
          statsManager={statsManager}
          isUpcomingFixture={isUpcomingFixture}
          onListingsClick={onListingsClick}
          eventType={eventType}
        />
      );
    }
    return (
      <FootballScoreCells
        eligibleSo5Scores={so5FixturePlayer.eligibleSo5Scores}
        statsManager={statsManager}
        isUpcomingFixture={isUpcomingFixture}
      />
    );
  })(eligibleSo5Scores.at(0));

  return (
    <StyledSearchTableRow
      key={id}
      as={ButtonBase}
      onClick={onClick}
      style={
        player.hasSo5LineupForFixture
          ? ({
              '--first-cell-background-color':
                'radial-gradient(40px 80% at -10px 50%,rgba(0, 255, 247, 0.3) 5%, var(--current-row-color) 100%), black',
              borderLeft: '1px solid rgba(0, 255, 247, 0.6)',
            } as CSSProperties)
          : undefined
      }
    >
      <SearchTableFirstCell>
        <LabelS color="var(--c-nd-600)">{index + 1}</LabelS>
        <PlayerCell>
          <PlayerAvatar player={player} variant="rounded" />
          <Vertical gap={0.5}>
            <Horizontal gap={0.5}>
              <LabelM bold>
                <Ellipsis>{player.displayName}</Ellipsis>
              </LabelM>
              {!!player.hasSo5LineupForFixture && (
                <LineupLocked size={2.5} color="var(--c-rewards-blue)" />
              )}
            </Horizontal>
            <BodyS as={Horizontal} color="var(--c-nd-600)">
              <TeamAvatar team={anyPlayerGameStats?.anyTeam} size={18} />
              {anyPlayerGameStats?.anyTeam.code}
              {' • '}
              {!!positionTyped && (
                <FormattedMessage {...anyPositionShortNames[positionTyped]} />
              )}
            </BodyS>
          </Vertical>
        </PlayerCell>
      </SearchTableFirstCell>
      {cells}
    </StyledSearchTableRow>
  );
};

FixtureTopPlayersTableRow.fragments = {
  fixturePlayer: gql`
    fragment FixtureTopPlayersTableRow_fixturePlayer on FixturePlayer {
      id
      score
      eligibleSo5Scores {
        id
        positionTyped
        scoreStatus
        score
        anyGame {
          id
          awayTeam {
            slug
            code
            ...TeamAvatar_team
          }
          homeTeam {
            slug
            code
            ...TeamAvatar_team
          }
        }
        anyPlayerGameStats {
          id
          anyTeam {
            slug
            code
            ...TeamAvatar_team
          }
        }
      }
      player {
        slug
        hasSo5LineupForFixture(so5FixtureSlug: $slug)
        ...GameplayPlayerAvatar_anyPlayer
      }
      ...BaseballScoreCells_fixturePlayer
      ...NBAScoreCells_fixturePlayer
      ...FootballScoreCells_fixturePlayer
    }
    ${PlayerAvatar.fragments.anyPlayer}
    ${TeamAvatar.fragments.team}
    ${BaseballScoreCells.fragments.fixturePlayer}
    ${NBAScoreCells.fragments.fixturePlayer}
    ${FootballScoreCells.fragments.fixturePlayer}
  ` as TypedDocumentNode<FixtureTopPlayersTableRow_fixturePlayer>,
};
